import React, { useEffect, useRef, useState } from "react";
import { Bot, Loader2, Send, RotateCcw, Sparkles, User } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Interactive chat with the trading coach (Claude Sonnet 4.5).
 * History is kept in-memory only (per browser session) - resets on reload.
 */
export default function CoachChat({ setupId }) {
  const [messages, setMessages] = useState([]); // [{role, content}]
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load persisted history from MongoDB whenever the setup changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setHistoryLoading(true);
      try {
        const r = await api.coachChatHistory(setupId);
        if (cancelled) return;
        const msgs = (r.messages || []).map((m) => ({ role: m.role, content: m.content }));
        setMessages(msgs);
      } catch {
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) {
          setHistoryLoading(false);
          setInput("");
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [setupId]);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;
    const next = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const r = await api.coachChat(setupId, next);
      setMessages([...next, { role: "assistant", content: r.reply || "(keine Antwort)" }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach antwortet nicht.");
      setMessages(next); // keep user msg
    } finally {
      setLoading(false);
      // refocus input
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const askInitial = () => sendMessage("Gib mir bitte eine kurze Einschätzung zum aktuellen Setup.");

  const reset = async () => {
    if (!window.confirm("Gesamten Chat-Verlauf löschen? (Kann nicht rückgängig gemacht werden)")) return;
    try {
      await api.coachChatClear(setupId);
      setMessages([]);
      setInput("");
      toast.success("Chat-Verlauf gelöscht.");
    } catch {
      toast.error("Konnte Verlauf nicht löschen.");
    }
  };

  return (
    <div
      className="border border-emerald-900/40 bg-zinc-950 flex flex-col"
      data-testid="coach-chat"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-emerald-900/30">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Bot size={14} className="text-emerald-400" />
          </div>
          <div>
            <div className="font-display font-bold text-sm tracking-tight">Trading Coach Chat</div>
            <div className="text-[10px] text-zinc-500 font-mono-num">25 Jahre Erfahrung · gespeichert in DB</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={reset}
              className="h-7 text-[11px] hover:bg-zinc-800 text-zinc-400"
              data-testid="btn-coach-reset"
              title="Verlauf zurücksetzen"
            >
              <RotateCcw size={12} />
            </Button>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 max-h-[420px] min-h-[180px]"
        data-testid="coach-chat-messages"
      >
        {historyLoading && (
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Loader2 size={12} className="animate-spin" /> Verlauf wird geladen…
          </div>
        )}
        {!historyLoading && messages.length === 0 && !loading && (
          <div className="flex flex-col items-start gap-3">
            <p className="text-xs text-zinc-400 leading-relaxed">
              Frag den Coach nach einer Einschätzung, oder erkläre ihm deinen Plan – er kennt das Setup, die
              Zonen und deinen aktuellen P&amp;L.
            </p>
            <Button
              onClick={askInitial}
              className="bg-emerald-600/90 hover:bg-emerald-500 text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider"
              data-testid="btn-coach-ask"
            >
              <Sparkles size={12} className="mr-2" /> Erste Einschätzung
            </Button>
            <div className="flex flex-wrap gap-1.5">
              <SuggestPill
                onClick={() =>
                  sendMessage("Ist das gerade ein guter Einstieg, oder soll ich auf den Pullback warten?")
                }
                text="Einstieg jetzt?"
              />
              <SuggestPill
                onClick={() => sendMessage("Wie sollte ich mein Risikomanagement bei diesem Setup gestalten?")}
                text="Risikomanagement"
              />
              <SuggestPill
                onClick={() => sendMessage("Wann sollte ich Teilgewinne mitnehmen?")}
                text="Teilgewinne"
              />
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <ChatBubble key={i} role={m.role} content={m.content} />
        ))}

        {loading && (
          <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="coach-chat-typing">
            <Loader2 size={12} className="animate-spin" /> Coach denkt nach…
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          sendMessage();
        }}
        className="border-t border-emerald-900/30 p-2 flex items-center gap-2"
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Frag den Coach… (z.B. ‚Der Entry ist erst geplant, nicht jetzt‘)"
          className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-emerald-700 outline-none text-sm px-3 py-2 rounded-sm font-mono-num text-zinc-100 placeholder:text-zinc-600"
          data-testid="coach-chat-input"
          disabled={loading}
        />
        <Button
          type="submit"
          disabled={loading || !input.trim()}
          className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm h-9 px-3"
          data-testid="coach-chat-send"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </Button>
      </form>
    </div>
  );
}

function SuggestPill({ text, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-1 border border-zinc-800 hover:border-emerald-700/60 hover:bg-emerald-950/30 text-[11px] text-zinc-300 rounded-sm transition-colors"
      data-testid={`coach-suggest-${text.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {text}
    </button>
  );
}

function ChatBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${role}`}>
      {!isUser && (
        <div className="w-6 h-6 shrink-0 bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mt-0.5">
          <Bot size={11} className="text-emerald-400" />
        </div>
      )}
      <div
        className={`max-w-[82%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap rounded-sm ${
          isUser
            ? "bg-zinc-100 text-zinc-900 font-mono-num"
            : "bg-zinc-900 border border-zinc-800 text-zinc-100"
        }`}
      >
        {content}
      </div>
      {isUser && (
        <div className="w-6 h-6 shrink-0 bg-zinc-800 border border-zinc-700 flex items-center justify-center mt-0.5">
          <User size={11} className="text-zinc-300" />
        </div>
      )}
    </div>
  );
}
